import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import _ from 'lodash';
import { useRecoilValue } from 'recoil';
import ClientSideConfigSelector from '../../../recoil/cashout/ClientSideConfigSelector';
import useHttpRequest from '../../api/HttpRequest';
import { getFormattedPrice, getProductFormattedNumber } from '../../helpers/CurrencyUtils';
import Loading from '../../general/Loading';
import CashoutHistoryTotal from './CashoutHistoryTotal';
import CashoutTransactionModal from './modals/CashoutTransactionModal';
import { capitalize } from '../../helpers/SubscriptionsUtil';

function normalizeTransaction(transaction, { cashoutCurrency } = {}) {
  const {
    products: orderProducts = [],
    ...rest
  } = transaction;

  const totalDiamonds = orderProducts.reduce((sum, orderProduct) => {
    const {
      product,
    } = orderProduct;

    if ((product.purchase.currency || '').toUpperCase() === 'DMD') {
      return sum + (Number(product.purchase.price) || 0);
    }
    return sum;
  }, 0);

  const cashoutOrderProduct = orderProducts
    .find((op) => op.product.exchange.currency === cashoutCurrency);

  const {
    destination: cashoutDestination = {},
    product: {
      exchange: cashoutExchange,
    },
  } = cashoutOrderProduct || {};

  const purchase = {
    price: totalDiamonds,
    currency: 'DMD',
  };

  return {
    ...rest,
    destination: cashoutDestination,
    purchase,
    exchange: cashoutExchange || {},
  };
}

function cashOutTransactions(transactions, params) {
  const {
    translation,
    cashoutCurrency,
  } = params;

  function isReversal(transaction) {
    const exchangePrice = Number(_.get(transaction, 'exchange.price'));
    return !Number.isNaN(exchangePrice) && exchangePrice < 0;
  }

  function cssStatus(txt) {
    switch (txt) {
      case 'FULFILLED': return 'complete';
      case 'CANCELLED': return 'failed';
      case 'ON_HOLD': return 'onhold';
      case 'PENDING_EXTERNAL': return 'submitted';
      case 'REVERSED': return 'returned';
      default: return 'pending';
    }
  }

  function translatedStatus(txt, t) {
    switch (txt) {
      case 'FULFILLED': return t('status_complete');
      case 'CANCELLED': return t('status_cancelled');
      case 'ON_HOLD': return t('status_onhold');
      case 'PENDING_EXTERNAL': return t('status_submitted');
      case 'REVERSED': return t('status_returned');
      default: return t('status_pending');
    }
  }

  function getProcessorId(account) {
    if (!account) return 'N/A';

    const [provider, type, target] = account.split(':');
    if (['paypal', 'tipalti', 'wire'].includes(provider)) {
      if (provider === 'wire') return 'Wire';
      const [network, accountHash, providerId] = target.split('_');
      return providerId || 'N/A';
    }
    return 'N/A';
  }

  const normalizedTransactions = transactions
    .map((tx) => normalizeTransaction(tx, { cashoutCurrency }));

  const filteredTransactions = _.filter(normalizedTransactions, (tx) => !isReversal(tx));

  const result = _.map(filteredTransactions, (transaction) => {
    const transactionDate = new Date(transaction.orderDate);
    return {
      id: transaction.orderId,
      date: transactionDate.toLocaleDateString(i18n.language),
      time: transactionDate.toLocaleTimeString(i18n.language),
      translatedStatus: translatedStatus(transaction.status, translation),
      cssStatus: cssStatus(transaction.status),
      user: transaction.purchaser,
      price: transaction.exchange.price || 0,
      diamonds: getProductFormattedNumber(transaction.purchase.price) || 0,
      processor: (getProcessorId(transaction.destination.account)),
      externalId: (transaction.destination.order || 'N/A'),
    };
  });

  return result;
}

export default function CashoutHistory() {
  const { t } = useTranslation(null, { useSuspense: false });
  const httpRequest = useHttpRequest();

  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState({ count: 0, totalAmount: 0 });
  const [loading, setLoading] = useState(true);

  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const config = useRecoilValue(ClientSideConfigSelector);
  const {
    live: {
      claimReward: {
        categories: {
          cashout: cashoutCurrency,
        },
        hideTransactionHistoryTotals = false,
      } = {},
    } = {},
  } = config || {};

  const currency = cashoutCurrency.toUpperCase();

  useEffect(() => {
    setLoading(true);

    try {
      const initializeHistory = async () => {
        const historyResponse = await httpRequest.send('/web-payments/cash-reward/orders/history');

        const summaryResponse = await httpRequest.send('/web-payments/cash-reward/orders/summary');

        /* The summary is aggregated per exchange currency, so an order bundled with a conversion
         * contributes its own bucket (e.g. 1 order worth 0 DMD) next to the cash-out one. Pick the
         * bucket matching the configured cash-out currency instead of whichever came back first.
         */
        const {
          exchange: {
            price = 0,
          } = {},
          count = 0,
        } = _.find(summaryResponse, (total) => (_.get(total, 'exchange.currency') || '').toUpperCase() === currency) || {};

        setHistory(cashOutTransactions(historyResponse.orders, {
          translation: t,
          cashoutCurrency: cashoutCurrency.toUpperCase(),
        }));
        setSummary({ count, totalAmount: price });
        setLoading(false);
      };

      initializeHistory();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error fetching cashout history...', err);
    }
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="global-layer ui-bootstrap member-diamonds-to-cash-history" style={{ display: 'block' }}>
      <div className="member-diamonds">
        {!hideTransactionHistoryTotals && (
          <CashoutHistoryTotal count={summary.count} totalAmount={summary.totalAmount} />
        )}
      </div>

      <div>
        <table className="table member-diamonds-to-cash-history-items">
          <thead>
            <tr>
              <th>{t('history_date')}</th>
              <th>{t('history_amount')}</th>
              <th>{t('history_status')}</th>
            </tr>
          </thead>
          <tbody>
            {history.map((it) => (
              <tr
                key={it.id}
                className="transaction-item"
                onClick={() => setSelectedTransaction(it)}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
              >
                <td className="diamond-history-transaction-date">{it.date}</td>
                <td className={`diamond-history-transaction-amount transaction-state-${it.cssStatus}`}>{getFormattedPrice(it.price, currency) }</td>
                <td className={`diamond-history-transaction-status transaction-state-${it.cssStatus}`}>{capitalize(it.translatedStatus)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {selectedTransaction && (
          <CashoutTransactionModal
            transaction={selectedTransaction}
            onClose={() => setSelectedTransaction(null)}
          />
        )}
      </div>
    </div>
  );
}
